import React, { useState, useEffect, useCallback } from 'react';

import { Badge } from '../../components/ui/badge';

import { AlertCircle, Clock, CheckCircle, XCircle } from 'lucide-react';

import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

import '../ProcesVerbaux.css';
import { getToken } from '../../services/tokenService';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const _API = `${BACKEND_URL}/api`;

export function useProcesVerbaux() {
  const { user: user } = useAuth();
  const [pvList, setPvList] = useState([]);
  const [souscriptions, setSouscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogKey, setDialogKey] = useState(0);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedPv, setSelectedPv] = useState(null);
  
  // Filtres et tri
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, en_temps, retard, gratuite_perdue
  const [sortField, setSortField] = useState('date_reception'); // date_reception, date_limite_depot, cout_ht_intervention
  const [sortOrder, setSortOrder] = useState('desc'); // asc, desc

  // Form state
  const [formData, setFormData] = useState({
    souscription_id: '',
    nom_chantier: '',
    type_operation: 'neuf',
    type_intervention: 'direct',
    cout_ht_intervention: '',
    date_debut_travaux: '',
    date_fin_travaux: '',
    date_reception: '',
    statut_reserves: 'sans_reserves', // sans_reserves ou avec_reserves
    // Maître d'ouvrage
    type_maitre_ouvrage: 'particulier', // 'particulier' ou 'professionnel'
    // Particulier
    mo_particulier_nom: '',
    mo_particulier_prenom: '',
    mo_particulier_adresse: '',
    mo_particulier_code_postal: '',
    mo_particulier_ville: '',
    mo_particulier_pays: 'France',
    mo_particulier_telephone: '',
    mo_particulier_email: '',
    // Adresse de l'opération (particulier)
    operation_adresse: '',
    operation_code_postal: '',
    operation_ville: '',
    operation_pays: 'France',
    // Professionnel
    mo_pro_raison_sociale: '',
    mo_pro_siret: '',
    mo_pro_code_postal: '',
    mo_pro_ville: '',
    mo_pro_pays: 'France',
    mo_pro_adresse: '',
    mo_pro_personne_contact: '',
    mo_pro_telephone: '',
    mo_pro_email: ''
  });

  // Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };

      const [pvResponse, souscriptionsResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/pv-chantiers/me`, { headers }),
        axios.get(`${BACKEND_URL}/api/souscriptions/me`, { headers })
      ]);

      // Trier du plus récent au plus ancien
      const sortByDate = (arr) => (arr || []).sort((a, b) => new Date(b.date_creation || b.created_at || 0) - new Date(a.date_creation || a.created_at || 0));
      setPvList(sortByDate(pvResponse.data));
      setSouscriptions(sortByDate(souscriptionsResponse.data.filter(s => s.is_active)));
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmitPv = async (e) => {
    e.preventDefault();
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };

      // Convertir les dates en format ISO
      const payload = {
        ...formData,
        cout_ht_intervention: parseFloat(formData.cout_ht_intervention),
        date_debut_travaux: new Date(formData.date_debut_travaux).toISOString(),
        date_fin_travaux: new Date(formData.date_fin_travaux).toISOString(),
        date_reception: new Date(formData.date_reception).toISOString()
      };

      await axios.post(`${BACKEND_URL}/api/pv-chantiers`, payload, { headers });
      
      alert('Procès-verbal créé avec succès ! Vous pouvez maintenant déposer le document.');
      setDialogOpen(false);
      setFormData({
        souscription_id: '',
        nom_chantier: '',
        type_operation: 'neuf',
        type_intervention: 'direct',
        cout_ht_intervention: '',
        date_debut_travaux: '',
        date_fin_travaux: '',
        date_reception: '',
        statut_reserves: 'sans_reserves',
        type_maitre_ouvrage: 'particulier',
        mo_particulier_nom: '',
        mo_particulier_prenom: '',
        mo_particulier_adresse: '',
        mo_particulier_code_postal: '',
        mo_particulier_ville: '',
        mo_particulier_pays: 'France',
        mo_particulier_telephone: '',
        mo_particulier_email: '',
        operation_adresse: '',
        operation_code_postal: '',
        operation_ville: '',
        operation_pays: 'France',
        mo_pro_raison_sociale: '',
        mo_pro_siret: '',
        mo_pro_adresse: '',
        mo_pro_code_postal: '',
        mo_pro_ville: '',
        mo_pro_pays: 'France',
        mo_pro_personne_contact: '',
        mo_pro_telephone: '',
        mo_pro_email: ''
      });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la création du PV');
    }
  };

  const handleUploadPv = async (pvId) => {
    if (!uploadFile) {
      alert('Veuillez sélectionner un fichier');
      return;
    }

    setUploadLoading(true);
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', uploadFile);

      await axios.post(
        `${BACKEND_URL}/api/pv-chantiers/${pvId}/depot`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      alert('Document déposé avec succès !');
      setUploadDialogOpen(false);
      setUploadFile(null);
      setSelectedPv(null);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors du dépôt du document');
    } finally {
      setUploadLoading(false);
    }
  };

  const getStatusBadge = (pv) => {
    if (pv.date_depot_pv) {
      if (pv.jours_retard === 0) {
        return <Badge className="bg-green-600 text-white font-semibold"><CheckCircle className="h-3 w-3 mr-1" />Dans les temps</Badge>;
      } else if (pv.jours_retard <= 30) {
        return <Badge className="bg-orange-600 text-white font-semibold"><Clock className="h-3 w-3 mr-1" />Retard ({pv.jours_retard}j)</Badge>;
      } else {
        return <Badge className="bg-red-600 text-white font-semibold"><XCircle className="h-3 w-3 mr-1" />Gratuité perdue</Badge>;
      }
    } else {
      const limitDate = new Date(pv.date_limite_depot);
      const now = new Date();
      if (now > limitDate) {
        return <Badge variant="destructive"><AlertCircle className="h-3 w-3 mr-1" />En retard</Badge>;
      } else {
        return <Badge className="bg-orange-600 text-white font-semibold"><Clock className="h-3 w-3 mr-1" />En attente</Badge>;
      }
    }
  };

  const getPenaliteText = (pv) => {
    if (!pv.date_depot_pv) return null;
    
    if (pv.remise_accordee > 0) {
      return <span className="text-green-600 font-semibold">Remise de {pv.remise_accordee.toFixed(2)}€</span>;
    } else if (pv.gratuite_accordee) {
      return <span className="text-red-600 font-semibold">Gratuité perdue : {pv.penalite_finale.toFixed(2)}€</span>;
    } else if (pv.penalite_finale > 0) {
      return <span className="text-orange-600 font-semibold">Pénalité : {pv.penalite_finale.toFixed(2)}€</span>;
    }
    return null;
  };

  // Fonctions de filtrage et tri
  const getFilteredAndSortedPV = useCallback(() => {
    let filtered = [...pvList];

    // Filtre par recherche textuelle
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(pv => 
        pv.nom_chantier.toLowerCase().includes(query) ||
        pv.type_operation.toLowerCase().includes(query) ||
        pv.type_intervention.toLowerCase().includes(query)
      );
    }

    // Filtre par statut
    if (filterStatus !== 'all') {
      filtered = filtered.filter(pv => {
        const status = getPVStatus(pv);
        return status === filterStatus;
      });
    }

    // Tri
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'date_reception':
          aValue = new Date(a.date_reception).getTime();
          bValue = new Date(b.date_reception).getTime();
          break;
        case 'date_limite_depot':
          aValue = new Date(a.date_limite_depot).getTime();
          bValue = new Date(b.date_limite_depot).getTime();
          break;
        case 'cout_ht_intervention':
          aValue = a.cout_ht_intervention;
          bValue = b.cout_ht_intervention;
          break;
        case 'nom_chantier':
          aValue = a.nom_chantier.toLowerCase();
          bValue = b.nom_chantier.toLowerCase();
          return sortOrder === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        default:
          return 0;
      }

      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });

    return filtered;
  }, [pvList, searchQuery, filterStatus, sortField, sortOrder]);

  const filteredPVList = getFilteredAndSortedPV();

  const getPVStatus = (pv) => {
    if (!pv.date_depot_pv) {
      const now = new Date();
      const dateLimit = new Date(pv.date_limite_depot);
      if (now > dateLimit) {
        return pv.gratuite_accordee ? 'gratuite_perdue' : 'retard';
      }
      return 'en_temps';
    }
    
    if (pv.gratuite_accordee) return 'gratuite_perdue';
    if (pv.jours_retard > 0) return 'retard';
    return 'en_temps';
  };

  return { user, dialogKey, dialogOpen, fetchData, filterStatus, filteredPVList, formData, getFilteredAndSortedPV, getPVStatus, getPenaliteText, getStatusBadge, handleInputChange, handleSubmitPv, handleUploadPv, loading, pvList, searchQuery, selectedPv, setDialogKey, setDialogOpen, setFilterStatus, setFormData, setLoading, setPvList, setSearchQuery, setSelectedPv, setSortField, setSortOrder, setSouscriptions, setUploadDialogOpen, setUploadFile, setUploadLoading, sortField, sortOrder, souscriptions, uploadDialogOpen, uploadFile, uploadLoading, user };
}
